// src/components/AdminDashboard.js
import React, { useEffect, useState } from 'react';
import { api } from '../utils/api';

const AdminDashboard = () => {
  const [presenze, setPresenze] = useState([]);
  const [utenti, setUtenti] = useState([]);
  const [utenteSelezionato, setUtenteSelezionato] = useState('tutti');
  const [paginaPrecedenti, setPaginaPrecedenti] = useState(1);
  const RECORDS_PER_PAGE = 30;

  const formatOra = (val) => {
    if (!val) return '-';
    const date = new Date(val);
    return isNaN(date.getTime()) || date.getFullYear() < 1970
      ? '-'
      : date.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
  };

  const formatData = (val) => {
    if (!val) return '-';
    const date = new Date(val);
    return isNaN(date.getTime()) || date.getFullYear() < 1970
      ? '-'
      : date.toLocaleDateString('it-IT');
  };

  const minutiToHHMM = (minuti) => {
    const h = Math.floor(minuti / 60);
    const m = minuti % 60;
    return `${h}h ${m.toString().padStart(2, '0')}m`;
  };

  useEffect(() => {
    const fetchData = async () => {
      const token = localStorage.getItem('token');
      try {
        const presenzeRes = await api.get('/presenze-tutti', {
          headers: { Authorization: `Bearer ${token}` },
        });
        setPresenze(presenzeRes.data);
        const utentiRes = await api.get('/utenti', {
          headers: { Authorization: `Bearer ${token}` },
        });
        setUtenti(utentiRes.data);
      } catch (err) {
        alert('Errore nel recupero dati.');
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    setPaginaPrecedenti(1);
  }, [utenteSelezionato, presenze.length]);

  const presenzeFiltrate = presenze.filter((p) => {
    return utenteSelezionato === 'tutti' || p.email === utenteSelezionato;
  });

  // Calcola l'inizio della settimana corrente (lunedì)
  const oggi = new Date();
  const giornoSettimana = oggi.getDay(); // 0=dom, 1=lun, ..., 6=sab
  const diff = giornoSettimana === 0 ? -6 : 1 - giornoSettimana;
  const lunedi = new Date(oggi);
  lunedi.setDate(oggi.getDate() + diff);
  lunedi.setHours(0, 0, 0, 0);
  const inizioTreSettimaneFa = new Date(lunedi);
  inizioTreSettimaneFa.setDate(lunedi.getDate() - 21);

  const settimanaCorrente = presenzeFiltrate.filter(p => {
    const data = new Date(p.data_presenza);
    return data >= lunedi;
  });

  const settimanePrecedenti = presenzeFiltrate.filter(p => {
    const data = new Date(p.data_presenza);
    return data >= inizioTreSettimaneFa && data < lunedi;
  });

  const totalePaginePrecedenti = Math.max(1, Math.ceil(settimanePrecedenti.length / RECORDS_PER_PAGE));
  const paginaPrecedentiSicura = Math.min(paginaPrecedenti, totalePaginePrecedenti);
  const precedentiPaginati = settimanePrecedenti.slice(
    (paginaPrecedentiSicura - 1) * RECORDS_PER_PAGE,
    paginaPrecedentiSicura * RECORDS_PER_PAGE
  );

  const renderRighe = (lista) => {
    return lista.map((p, i) => {
          const totale = p.ore_calcolate_minuti ?? 0;

      return (
        <tr key={`${p.email}-${p.data_presenza}-${i}`}>
          <td>{p.nome}</td>
          <td>{p.email}</td>
          <td>{formatData(p.data_presenza)}</td>
          <td>{formatOra(p.entrata_matt)}</td>
          <td>{formatOra(p.uscita_matt)}</td>
          <td>{formatOra(p.entrata_pom)}</td>
          <td>{formatOra(p.uscita_pom)}</td>
          <td>{totale > 0 ? minutiToHHMM(totale) : '-'}</td>
        </tr>
      );
    });
  };

  return (
    <div>
      <h2>📊 Area Admin - Presenze</h2>

      <div style={{ margin: '20px 0' }}>
        <label>👤 Filtra per utente:</label>
        <select value={utenteSelezionato} onChange={(e) => setUtenteSelezionato(e.target.value)}>
          <option value="tutti">Tutti</option>
          {utenti.map((u) => (
            <option key={u.user_id} value={u.email}>
              {u.nome} ({u.email})
            </option>
          ))}
        </select>
      </div>

      <h3 style={{ marginTop: '40px' }}>🟢 Settimana corrente</h3>
      <table border="1" cellPadding="6" style={{ marginBottom: '30px', width: '100%' }}>
        <thead>
          <tr>
            <th>Nome</th>
            <th>Email</th>
            <th>Data</th>
            <th>Entrata Mattina</th>
            <th>Uscita Mattina</th>
            <th>Entrata Pomeriggio</th>
            <th>Uscita Pomeriggio</th>
            <th>Ore Totali</th>
          </tr>
        </thead>
        <tbody>{renderRighe(settimanaCorrente)}</tbody>
      </table>

      <hr style={{ borderTop: '2px solid #666', margin: '30px 0' }} />

      <h3>📅 Settimane precedenti (ultime 3)</h3>
      <table border="1" cellPadding="6" style={{ width: '100%' }}>
        <thead>
          <tr>
            <th>Nome</th>
            <th>Email</th>
            <th>Data</th>
            <th>Entrata Mattina</th>
            <th>Uscita Mattina</th>
            <th>Entrata Pomeriggio</th>
            <th>Uscita Pomeriggio</th>
            <th>Ore Totali</th>
          </tr>
        </thead>
        <tbody>{renderRighe(precedentiPaginati)}</tbody>
      </table>
      {settimanePrecedenti.length > 0 && (
        <div style={{ marginTop: '12px', display: 'flex', gap: '8px', alignItems: 'center' }}>
          <button
            onClick={() => setPaginaPrecedenti((p) => Math.max(1, p - 1))}
            disabled={paginaPrecedentiSicura <= 1}
          >
            ← Precedente
          </button>
          <span>
            Pagina {paginaPrecedentiSicura} di {totalePaginePrecedenti} ({settimanePrecedenti.length} record)
          </span>
          <button
            onClick={() => setPaginaPrecedenti((p) => Math.min(totalePaginePrecedenti, p + 1))}
            disabled={paginaPrecedentiSicura >= totalePaginePrecedenti}
          >
            Successiva →
          </button>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
